import { Dispatch, FC, ReactNode, SetStateAction, createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react'
import { POST_WATERMARK, VisualFileType } from 'constants/visual'
import { TypeOfAvailableFileTypes, VisualTypeSelectEnumType, useGalleryConstants } from './GalleryConstants.context'

import { useAuth0 } from 'utils/auth'
import { useGalleryAssignment } from './GalleryAssignment.context'
import { useRef } from 'react'

interface GalleryVisualTypeInterface {
  visualTypeSelect: VisualFileType.RAW | VisualFileType.POST | typeof POST_WATERMARK
  setVisualTypeSelect: Dispatch<SetStateAction<VisualFileType.RAW | VisualFileType.POST | typeof POST_WATERMARK>>
  originalType: VisualFileType.RAW | VisualFileType.POST | typeof POST_WATERMARK
  thumbnailType: VisualFileType.RAW_THUMB | VisualFileType.POST_THUMB | VisualFileType.POST_THUMB_WATERMARK
  webType: VisualFileType.RAW_WEB | VisualFileType.POST_WEB | VisualFileType.POST_WEB_WATERMARK
  normalizedOriginalType: VisualFileType.RAW | VisualFileType.POST
  fileTypes: Set<TypeOfAvailableFileTypes>
  setFileTypes: Dispatch<SetStateAction<Set<TypeOfAvailableFileTypes>>>
  downloadableTypes: Array<'WEB' | 'HD'>
  transformTypes: () => (VisualFileType.POST | VisualFileType.POST_WEB)[]
  transformAdminTypes: () => (VisualFileType.RAW | VisualFileType.RAW_WEB | VisualFileType.POST | VisualFileType.POST_WEB | VisualFileType.POST_WEB_WATERMARK)[]
}

const defaultGalleryVisualTypeValue: GalleryVisualTypeInterface = {
  visualTypeSelect: POST_WATERMARK,
  setVisualTypeSelect: () => { throw new Error('setVisualTypeSelect is undefined') },
  originalType: POST_WATERMARK,
  thumbnailType: VisualFileType.POST_THUMB_WATERMARK,
  webType: VisualFileType.POST_WEB_WATERMARK,
  normalizedOriginalType: VisualFileType.POST,
  fileTypes: new Set(),
  downloadableTypes: ['HD'],
  setFileTypes: () => { throw new Error('setFileTypes is undefined') },
  transformTypes: () => { throw new Error('transformTypes is undefined') },
  transformAdminTypes: () => { throw new Error('transformAdminTypes is undefined') },
}

/** Gallery visual type context */
export const GalleryVisualTypeContext = createContext<GalleryVisualTypeInterface>(defaultGalleryVisualTypeValue)
/** Gallery visual type context hook */
export const useGalleryVisualType = (): GalleryVisualTypeInterface => useContext(GalleryVisualTypeContext)

/** Context provider for gallery visual type */
export const GalleryVisualTypeContextProvider: FC<{
  assignmentId: string
  children?: ReactNode
}> = ({
  assignmentId,
  children,
}) => {
    const { roles } = useAuth0()
    const {
      VisualTypeSelectEnum,
      AvailableFileTypes,
      originalTypeMap,
      thumbnailTypeMap,
      webTypeMap,
    } = useGalleryConstants()
    const { canSelectVisualsForStaging, product, assignmentStage } = useGalleryAssignment()
    const { stagesUnlockingCreativeSendAssignmentVisualsToAdmin } = useGalleryConstants()

    const [visualTypeSelect, setVisualTypeSelect] = useState<VisualTypeSelectEnumType>(roles.isCreative ? VisualTypeSelectEnum.RAW : (roles.isClient ? ((!canSelectVisualsForStaging) ? VisualTypeSelectEnum.RAW : VisualTypeSelectEnum.POST_WATERMARK) : VisualTypeSelectEnum.POST))

    /**
     * An assignment whose visuals have not been submitted yet is worked on in the RAW view — that is where the
     * upload and submit controls live. Landing an admin on POST hides the only controls that apply, which
     * reads as "there is nothing I can do here". Runs once, so a deliberate switch is not undone.
     */
    const defaultedToRaw = useRef(false)
    useLayoutEffect(() => {
      if (defaultedToRaw.current || !roles.isAdmin || !assignmentStage) return
      defaultedToRaw.current = true
      if (stagesUnlockingCreativeSendAssignmentVisualsToAdmin.has(assignmentStage)) {
        setVisualTypeSelect(VisualTypeSelectEnum.RAW)
      }
    }, [roles.isAdmin, assignmentStage, stagesUnlockingCreativeSendAssignmentVisualsToAdmin, VisualTypeSelectEnum.RAW])

    const originalType = useMemo(() => originalTypeMap[visualTypeSelect], [visualTypeSelect, originalTypeMap])

    const thumbnailType = useMemo(() => {
      if (roles.isClient && visualTypeSelect === VisualTypeSelectEnum.RAW) {
        return VisualFileType.POST_THUMB_WATERMARK
      }

      return thumbnailTypeMap[visualTypeSelect]
    }, [roles.isClient, visualTypeSelect, VisualTypeSelectEnum.RAW, thumbnailTypeMap])

    const webType = useMemo(() => {
      if (roles.isClient && visualTypeSelect === VisualTypeSelectEnum.RAW) {
        return VisualFileType.POST_WEB_WATERMARK
      }

      return webTypeMap[visualTypeSelect]
    }, [VisualTypeSelectEnum.RAW, roles.isClient, visualTypeSelect, webTypeMap])

    const normalizedOriginalType = useMemo(() => {
      if (roles.isClient) {
        return VisualFileType.POST
      }

      switch (originalType) {
        case POST_WATERMARK:
          return VisualFileType.POST
        default:
          return originalType
      }
    }, [originalType, roles.isClient])

    const [fileTypes, setFileTypes] = useState<Set<TypeOfAvailableFileTypes>>(new Set([AvailableFileTypes.WEB, AvailableFileTypes.HD]))

    const transformTypes = useCallback(() => Array.from(fileTypes).map(type => {
      switch (type) {
        case AvailableFileTypes.HD:
          return VisualFileType.POST
        default:
          return VisualFileType.POST_WEB
      }
    }), [fileTypes, AvailableFileTypes])

    const transformAdminTypes = useCallback(() => Array.from(fileTypes).map(type => {
      switch (type) {
        case AvailableFileTypes.HD:
          return normalizedOriginalType
        default:
          return webType
      }
    }), [fileTypes, AvailableFileTypes, normalizedOriginalType, webType])

    const downloadableTypes: Array<'WEB' | 'HD'> = useMemo(() => {
      // Floor plans (Matterport-with-visit included, by product ruling) and videos have no WEB variant.
      if (product?.isFloorPlan || product?.isVideo) {
        return ['HD']
      }
      return ['WEB', 'HD']
    }, [product])

    // Change visual type with change of role
    useLayoutEffect(() => {
      if (roles.isCreative) {
        setVisualTypeSelect(VisualTypeSelectEnum.RAW)
      } else if (roles.isClient) {
        setVisualTypeSelect((!canSelectVisualsForStaging) ? VisualTypeSelectEnum.RAW : VisualTypeSelectEnum.POST_WATERMARK)
      } else {
        setVisualTypeSelect(VisualTypeSelectEnum.POST)
      }
    }, [roles, setVisualTypeSelect, VisualTypeSelectEnum, canSelectVisualsForStaging])

    return (
      <GalleryVisualTypeContext.Provider
        value={{
          visualTypeSelect,
          setVisualTypeSelect,
          originalType,
          thumbnailType,
          webType,
          normalizedOriginalType,
          fileTypes,
          setFileTypes,
          transformTypes,
          transformAdminTypes,
          downloadableTypes
        }}
      >
        {children}
      </GalleryVisualTypeContext.Provider>
    )
  }
